import { useEffect, useMemo, useRef } from "react";

export type ShortcutHandler = (e: KeyboardEvent) => void;

export interface Shortcut {
  /** Lowercase key name (e.g. "j", "arrowdown", " ", "escape", "?", "1") */
  key: string;
  handler: ShortcutHandler;
  /** Allow firing while focus is in an input/textarea/contenteditable. Default false. */
  allowInInput?: boolean;
  /** Description for the cheat-sheet UI. */
  description?: string;
  /** Group label for the cheat-sheet UI. */
  group?: string;
}

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

function normalizeKey(e: KeyboardEvent): string {
  if (e.key === " ") return " ";
  return e.key.toLowerCase();
}

/**
 * Register a list of keyboard shortcuts on `window`.
 * Pass `enabled=false` to temporarily disable without remounting.
 */
export function useReviewKeyboardShortcuts(
  shortcuts: Shortcut[],
  enabled = true
) {
  const ref = useRef(shortcuts);
  ref.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      // Ignore modifier-key combos so we don't conflict with browser shortcuts.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const inEditable = isEditableTarget(e.target);
      const key = normalizeKey(e);
      for (const s of ref.current) {
        if (s.key !== key) continue;
        if (inEditable && !s.allowInInput) continue;
        s.handler(e);
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);

  // Stable list for cheat-sheet UI consumers.
  return useMemo(() => shortcuts.filter((s) => s.description), [shortcuts]);
}
